import { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  TextField,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  CalendarToday as CalendarIcon,
  FitnessCenter as FitnessCenterIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';

// Import Hooks and Types
import { 
  useGetWorkoutByIdQuery, 
  useGetWorkoutCourseQuery, 
  useUpdateWorkoutMutation 
} from '../../../store/api/baseApi';
import WorkoutStructureBuilder from '../components/WorkoutStructureBuilder'
import type {
  WorkoutStatus
} from '../../../store/api/models/workoutModels';

const WorkoutDetailPage = () => {
  const { workoutId } = useParams<{ workoutId: string }>();

  // API Hooks
  const { data: workout, isLoading, error, refetch } = useGetWorkoutByIdQuery(workoutId!, {
    skip: !workoutId,
  });
  const [updateWorkout, { isLoading: isUpdating }] = useUpdateWorkoutMutation();

  // We wait for workout to load so we have the course_id, and we skip if there isn't one
  const { data: courseTemplate } = useGetWorkoutCourseQuery(
    workout?.course_id, 
    { skip: !workout?.course_id }
  );

  const [notes, setNotes] = useState('');
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [newScheduledDate, setNewScheduledDate] = useState('');

  // Sync notes when workout data loads
  useEffect(() => {
    if (workout?.notes) {
      setNotes(workout.notes);
    }
  }, [workout]);

  const handleStatusChange = async (newStatus: WorkoutStatus) => {
    if (!workout || !workoutId) return;
    try {
      await updateWorkout({ id: workoutId, data: { status: newStatus } }).unwrap();
      refetch();
    } catch (err) {
      console.error('Error updating workout status:', err);
    }
  };

  const handleReschedule = async () => {
    if (!workout || !newScheduledDate || !workoutId) return;
    try {
      // No @ts-ignore needed anymore because we added scheduled_at to WorkoutUpdate
      await updateWorkout({ 
        id: workoutId, 
        data: { 
          status: workout.status,
          notes: workout.notes || undefined,
          scheduled_at: newScheduledDate 
        } 
      }).unwrap();
      setRescheduleDialogOpen(false);
      refetch();
    } catch (err) {
      console.error('Error rescheduling workout:', err);
    }
  };

  const handleNotesUpdate = async () => {
    if (!workout || !workoutId) return;
    try {
      await updateWorkout({ id: workoutId, data: { notes } }).unwrap();
      refetch();
    } catch (err) {
      console.error('Error updating notes:', err);
    }
  };

  const handleDescriptionUpdate = async () => {
    if (!workout || !workoutId) return;
    try {
      await updateWorkout({ id: workoutId, data: { description } }).unwrap();
      refetch();
    } catch (err) {
      console.error('Error updating description:', err);
    }
  };

  // --- Display Helpers ---

  const getStatusColor = (status: WorkoutStatus): 'success' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      case 'in_progress': return 'info';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string | null | undefined): 'error' | 'warning' | 'info' => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      default: return 'info';
    }
  };

  // --- Render Helpers for Structure ---
  const renderStructure = () => {
    // If we have a linked course, use its structure. 
    // Otherwise, check if the workout instance has its own (rare/legacy fallback).
    const structureData = courseTemplate ? courseTemplate.structure : workout?.structure;

    // Check if data exists and has items
    if (!structureData || structureData.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <FitnessCenterIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography>No workout structure defined.</Typography>
        </Box>
      );
    }

    // Pass the valid structure to the Builder
    return (
      <WorkoutStructureBuilder 
        initialStructure={structureData} 
        onChange={() => {}} // Read-only
        readOnly={true} 
      />
    );
  };

  // --- Loading & Error States ---

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Failed to load workout details</Alert>
      </Container>
    );
  }

  if (!workout) {
     return null;
  }

  // Check if this workout is linked to a course template
  const hasCourseLink = !!workout.course_id;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      
      {/* Workout Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={8}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              {/* Editable Course Link Logic */}
              {hasCourseLink ? (
                <Link
                  component={RouterLink}
                  to={`/trainer/templates/edit/${workout.course_id}`} // Fixed: course_id is number
                  underline="hover"
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  <Tooltip title="Click to edit Course Template">
                    <Typography variant="h4" sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      {workout.name || 'Untitled Workout'}
                      <OpenInNewIcon fontSize="medium" />
                    </Typography>
                  </Tooltip>
                </Link>
              ) : (
                <Typography variant="h4">
                  {workout.name || 'Untitled Workout'}
                </Typography>
              )}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, gap: 1 }}>
              <Chip
                label={workout.status}
                color={getStatusColor(workout.status)}
                icon={
                  workout.status === 'completed' ? (
                    <CheckCircleIcon />
                  ) : workout.status === 'cancelled' ? (
                    <CancelIcon />
                  ) : (
                    <ScheduleIcon />
                  )
                }
                size="small"
              />
              <Chip 
                label={workout.priority ? workout.priority.toUpperCase() : 'NO PRIORITY'} 
                color={getPriorityColor(workout.priority)} 
                size="small"
              />
              <Chip label={`ID: ${workout.id}`} variant="outlined" size="small" />
              {workout.course_id && (
                <Chip 
                  label={`Course ID: ${workout.course_id}`} 
                  variant="outlined" 
                  size="small" 
                  color="info"
                  icon={<FitnessCenterIcon />}
                />
              )}
            </Stack>
          </Grid>
          
          <Grid item xs={12} md={4} textAlign={{ xs: 'left', md: 'right' }}>
             <Typography variant="body2" color="text.secondary" gutterBottom display="flex" alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <CalendarIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Scheduled: {workout.scheduled_at ? format(parseISO(workout.scheduled_at), 'PPP p') : 'TBD'}
            </Typography>
            {workout.assigned_by_trainer_id && (
               <Typography variant="caption" color="text.secondary">
                 Assigned by Trainer ID: {workout.assigned_by_trainer_id}
               </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Description Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Description
        </Typography>
        {workout?.description ? (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {workout.description}
          </Typography>
        ) : (
          <Typography variant="body1" color="text.secondary" fontStyle="italic">
            No description provided.
          </Typography>
        )}
      </Paper>

      {/* Workout Structure (Read Only) */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Workout Structure</Typography>
          {hasCourseLink && (
             <Button 
               size="small" 
               variant="text" 
               component={RouterLink} 
               to={`/trainer/templates/edit/${workout.course_id}`}
               endIcon={<EditIcon />}
             >
               Edit Template
             </Button>
          )}
        </Stack>
        
        <Divider sx={{ mb: 2 }} />
        
        {renderStructure()}
      </Paper>

      {/* Notes Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Notes
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about this workout..."
          disabled={isUpdating}
        />
        <Box mt={2}>
          <Button
            variant="contained"
            onClick={handleNotesUpdate}
            disabled={notes === workout.notes || isUpdating}
          >
            {isUpdating ? 'Saving...' : 'Save Notes'}
          </Button>
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Actions
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
          {workout.status === 'planned' && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleStatusChange('completed')}
                disabled={isUpdating}
              >
                Mark Complete
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => handleStatusChange('cancelled')}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                startIcon={<ScheduleIcon />}
                onClick={() => setRescheduleDialogOpen(true)}
                disabled={isUpdating}
              >
                Reschedule
              </Button>
            </>
          )}
          
          {workout.activity_id && (
             <Button
               component={RouterLink}
               to={`/activities/${workout.activity_id}`}
               variant="outlined"
               startIcon={<LinkIcon />}
             >
               View Linked Activity
             </Button>
          )}
        </Stack>
      </Paper>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialogOpen} onClose={() => setRescheduleDialogOpen(false)}>
        <DialogTitle>Reschedule Workout</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="datetime-local"
            label="New Date & Time"
            value={newScheduledDate}
            onChange={(e) => setNewScheduledDate(e.target.value)}
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReschedule}
            variant="contained"
            disabled={!newScheduledDate || isUpdating}
          >
            {isUpdating ? 'Rescheduling...' : 'Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default WorkoutDetailPage;